import { useState, useEffect } from 'react';
import { useApp } from '@/context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Skeleton } from '@/components/ui/skeleton';
import { Calendar as CalendarIcon, Clock, MapPin, BookOpen, Flame } from 'lucide-react';
import { format, isSameDay, startOfMonth, endOfMonth, eachDayOfInterval } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export const CalendarioPage = () => {
  const { celulas, loadEncontros, igrejaAtual, loadingCelulas } = useApp();
  
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [allEncontros, setAllEncontros] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadAllEncontros = async () => {
      if (!igrejaAtual || celulas.length === 0) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      const encontrosPromises = celulas.map(celula => loadEncontros(celula.id));
      const results = await Promise.all(encontrosPromises);
      
      // Flatten and add celula info
      const flatEncontros = results.flatMap((encontros, index) => 
        (encontros || []).map(e => ({ ...e, celula: celulas[index] }))
      );
      
      setAllEncontros(flatEncontros);
      setLoading(false);
    };
    
    loadAllEncontros();
  }, [celulas, igrejaAtual]);

  // Get encontros for selected date
  const encontrosNoDia = allEncontros.filter(e => 
    isSameDay(new Date(e.data), selectedDate)
  );

  // Get dates with encontros for highlighting
  const datasComEncontros = allEncontros.map(e => new Date(e.data));

  // Custom day render for calendar
  const modifiers = {
    hasEncontro: datasComEncontros,
  };

  const modifiersStyles = {
    hasEncontro: {
      backgroundColor: 'hsl(var(--primary) / 0.1)',
      color: 'hsl(var(--primary))',
      fontWeight: 'bold',
    },
  };

  return (
    <div className="page-transition">
      {/* Header */}
      <div className="px-6 lg:px-8 py-8 bg-gradient-to-r from-primary/10 via-background to-purple-500/10 border-b border-border">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <Badge variant="secondary" className="text-xs">
              {igrejaAtual?.nome || 'Igreja'}
            </Badge>
          </div>
          <h1 className="font-heading text-3xl lg:text-4xl font-bold text-foreground">
            Calendário
          </h1>
          <p className="text-muted-foreground mt-1">
            Visualize todos os encontros das células
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 lg:px-8 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Calendar */}
            <Card className="lg:col-span-2 card-elevated">
              <CardContent className="p-6">
                {loading || loadingCelulas ? (
                  <Skeleton className="h-[400px] w-full rounded-lg" />
                ) : (
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => date && setSelectedDate(date)}
                    locale={ptBR}
                    modifiers={modifiers}
                    modifiersStyles={modifiersStyles}
                    className="rounded-md border"
                  />
                )}
              </CardContent>
            </Card>

            {/* Sidebar - Encontros do dia */}
            <div className="space-y-6">
              <Card className="card-elevated">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CalendarIcon className="w-5 h-5 text-primary" />
                    {format(selectedDate, "d 'de' MMMM", { locale: ptBR })}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="space-y-4">
                      <Skeleton className="h-20 w-full" />
                      <Skeleton className="h-20 w-full" />
                    </div>
                  ) : encontrosNoDia.length === 0 ? (
                    <div className="text-center py-8">
                      <Flame className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                      <p className="text-muted-foreground">
                        Nenhum encontro neste dia
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {encontrosNoDia.map((encontro) => (
                        <div 
                          key={encontro.id}
                          className="p-4 rounded-lg bg-muted/50 border border-border"
                        >
                          <div className="flex items-start gap-3">
                            <div className="p-2 rounded-lg bg-primary/10">
                              <BookOpen className="w-4 h-4 text-primary" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium text-foreground truncate">
                                {encontro.celula?.nome || 'Célula'}
                              </h4>
                              <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                                <Clock className="w-3 h-3" />
                                {encontro.horario}
                              </div>
                              {encontro.local && (
                                <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                                  <MapPin className="w-3 h-3" />
                                  <span className="truncate">{encontro.local}</span>
                                </div>
                              )}
                              {encontro.livro && (
                                <p className="text-sm text-muted-foreground mt-1">
                                  📖 {encontro.livro} {encontro.capitulo}
                                </p>
                              )}
                              <Badge 
                                variant={encontro.status === 'realizado' ? 'secondary' : 'default'}
                                className="mt-2"
                              >
                                {encontro.status === 'realizado' ? 'Realizado' : 'Agendado'}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Legenda */}
              <Card className="card-elevated">
                <CardHeader>
                  <CardTitle className="text-sm">Legenda</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded bg-primary/10 border border-primary/20" />
                      <span className="text-muted-foreground">Dias com encontros</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded bg-primary" />
                      <span className="text-muted-foreground">Dia selecionado</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
